import { type RefObject, useEffect, useState } from 'react'

export interface ScrollState {
  /** Its content is taller than it: it scrolls, so it must take the focus (WCAG 2.1.1). */
  scrolls: boolean
  /** More of it lies below what shows: its foot fades (`.scroll-fade[data-more]`). */
  moreBelow: boolean
}

const NONE: ScrollState = { scrolls: false, moreBelow: false }

/**
 * Whether an element that scrolls inside the page does, and whether more of
 * it lies below what shows: the sidebar's menu, the hand-over's rail. Read
 * again on its own scroll, when it or a child changes size (the window, the
 * sidebar showing at all), and after every render of its owner, which can
 * change what it holds without changing its size.
 */
export function useScrollState(ref: RefObject<HTMLElement | null>): ScrollState {
  const [state, setState] = useState<ScrollState>(NONE)
  const update = () => {
    const element = ref.current
    if (!element) return
    // A pixel of slack: a zoomed page can stop a fraction short of the end.
    const scrolls = element.scrollHeight > element.clientHeight + 1
    const moreBelow = element.scrollTop + element.clientHeight < element.scrollHeight - 1
    setState((now) =>
      now.scrolls === scrolls && now.moreBelow === moreBelow ? now : { scrolls, moreBelow },
    )
  }
  // After every render: what it holds can change without its size changing.
  useEffect(update)
  // biome-ignore lint/correctness/useExhaustiveDependencies: `update` reads the ref, which is stable
  useEffect(() => {
    const element = ref.current
    if (!element) return
    element.addEventListener('scroll', update, { passive: true })
    const resize = new ResizeObserver(update)
    resize.observe(element)
    for (const child of element.children) resize.observe(child)
    return () => {
      element.removeEventListener('scroll', update)
      resize.disconnect()
    }
  }, [ref])
  return state
}
